import { useCallback, useEffect, useRef, useState } from 'react'

type EffectiveConnectionType = 'slow-2g' | '2g' | '3g' | 'slow-4g' | '4g'

interface NetworkInformation extends EventTarget {
  readonly effectiveType?: EffectiveConnectionType
  addEventListener(type: 'change', listener: () => void): void
  removeEventListener(type: 'change', listener: () => void): void
}

declare global {
  interface Navigator {
    connection?: NetworkInformation
    mozConnection?: NetworkInformation
    webkitConnection?: NetworkInformation
  }
}

const PING_URL = 'https://jsonplaceholder.typicode.com/posts'
const CHECK_INTERVAL = 10000
const SLOW_THRESHOLD = 90
const FETCH_TIMEOUT = 3000
const SLOW_CHECK_LIMIT = 5

export function useNetworkStatus() {
  const [isOffline, setIsOffline] = useState(() => !navigator.onLine)
  const [isPoorConnection, setIsPoorConnection] = useState(false)

  const isCheckingRef = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const slowCountRef = useRef(0)

  const getConnection = useCallback((): NetworkInformation | undefined => {
    return (
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection
    )
  }, [])

  const checkNetworkStatus = useCallback(async () => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT)

    try {
      const response = await fetch(PING_URL, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store'
      })

      const startTime = performance.now()
      await response.text()
      clearTimeout(timeoutId)

      const duration = performance.now() - startTime
      const isSlow = duration > SLOW_THRESHOLD

      return {
        isOffline: false,
        isPoorConnection: isSlow
      }
    } catch {
      clearTimeout(timeoutId)
      return {
        isOffline: true,
        isPoorConnection: true
      }
    }
  }, [])

  const updateNetworkStatus = useCallback(async () => {
    if (isCheckingRef.current) return
    isCheckingRef.current = true

    const { isOffline, isPoorConnection } = await checkNetworkStatus()
    setIsOffline(isOffline)

    if (isPoorConnection) {
      slowCountRef.current += 1
      if (slowCountRef.current >= SLOW_CHECK_LIMIT) {
        setIsPoorConnection(true)
      }
    } else {
      slowCountRef.current = 0
      setIsPoorConnection(false)
    }

    isCheckingRef.current = false
  }, [checkNetworkStatus])

  useEffect(() => {
    updateNetworkStatus()

    intervalRef.current = setInterval(updateNetworkStatus, CHECK_INTERVAL)

    const handleOnline = () => updateNetworkStatus()
    const handleOffline = () => {
      setIsOffline(true)
      setIsPoorConnection(true)
    }
    const handleConnectionChange = () => updateNetworkStatus()

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    const connection = getConnection()
    connection?.addEventListener('change', handleConnectionChange)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      connection?.removeEventListener('change', handleConnectionChange)
    }
  }, [updateNetworkStatus, getConnection])

  return { isOffline, isPoorConnection }
}
